import { useId, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react'

import type {
  ChamberRow,
  ModuleSnapshot,
  ReagentDef,
  RecipeLabwareDef,
  TrayCell,
  VisionFeed,
} from '@ctcells/contract'
import { enumLabel, moduleLabel, t, type MessageKey } from '@ctcells/i18n'

import { Badge } from '../../atoms/Badge.tsx'
import { cx } from '../../atoms/cx.ts'
import { Icon } from '../../atoms/Icon.tsx'
import { StaleMark } from '../../atoms/StaleMark.tsx'
import { StageTrack } from '../../atoms/StageTrack.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { figureTabs } from '../../layout/figureTabs.ts'
import { canView, type ScreenAccess } from '../access.ts'
import { staleDetail, staleValue } from '../format.ts'

import { ScreenState } from '../ScreenState.tsx'
import { EquipmentSheet } from './EquipmentSheet.tsx'
import { VisionPane } from './VisionColumn.tsx'
import { isVisionDevice, VISION_DEVICES } from './visionColumnModel.ts'
import { useFollowRunningDevice } from './followRunningDevice.ts'
import { ModuleFigureView } from './ModuleFigureView.tsx'
import type { OverviewParts } from './FocusFigure.tsx'
import {
  activeAlarmCount,
  findModule,
  holdKindOf,
  monitoringDeviceName,
  samplesAt,
  stageStateOf,
  type MonitoringDeviceKey,
} from './monitoringModel.ts'
import type { SampleBundle } from './patientChambers.ts'
import { ChamberActivityContext, chamberActivityOf } from './chamberActivity.ts'
import { SampleBundles } from './SampleBundles.tsx'
import { useColumnShown, type MonitorColumn } from './monitoringColumns.ts'
import { useNarrowFlag } from './useBoxMeasure.ts'
import { SupplyPanel } from './SupplyPanel.tsx'
import type { ReplenishView } from './SupplyReplenishDialog.tsx'
import type { SupplyKey, SupplySummary } from './supplySummary.ts'
import { stageOfDevice, type FlowCell, type StageTally } from './sampleFlowModel.ts'

import '../screens.css'
import './monitoring.css'

/**
 * 운영 › 모니터링 — 와이어프레임 `dash()` 1503행 · README §14-3.
 *
 * ```
 * ┌ 모듈 카드 줄 (6축 레일 + 원심 · 어셈블러 · 염색기) ─────────────────────┐
 * ├ .fighead ───────────────────── [전체][트레이][원심분리기][비전][3축 로봇]┤
 * │ 포커스 뷰(제목 없음)            │ 사이드 패널 440px (제어 버튼 없음)      │
 * └──────────────────────────────┴────────────────────────────────────────┘
 * ```
 *
 * 🔴 **「전체」는 그림만이다.** 메트릭 카드가 없고 오른쪽 아래 범례 상자만 있다 —
 *    와이어프레임 전체에서 범례가 허용된 유일한 자리다(§1-2).
 * 🔴 **사이드 패널에 제어 버튼이 없다.** 조작은 개발 › 제어에서만 한다(§1-3).
 * 🔴 **탭은 패널 오른쪽 위 고정.** `FigurePanel` 이 그 자리를 쥔다 — 옮기지 마라.
 * 🔴 모듈 탭을 바꾸면 사이드 패널이 닫힌다(§14-3).
 */
export interface MonitoringScreenProps {
  modules?: readonly ModuleSnapshot[]
  /**
   * 이 모듈에 있는 시료. **`GET /samples` 에서 온다** — live 컨테이너가 `current_module` 로 고른다.
   * 안 넘기면 빈 목록이다(시료 구역이 「표시할 수 없음」으로 떨어진다).
   */
  chambers?: readonly ChamberRow[]
  /**
   * 🔴 **시료를 «못 봤을» 때의 사유.** 넘어오면 `chambers` 가 빈 것은 「없다」가 아니라
   *    **「모른다」**이고, 화면은 그 사실을 두 곳에 반영한다 —
   *    ⓐ 모듈 카드의 「시료 N건」을 **아예 안 적는다**(0 건으로 접지 않는다)
   *    ⓑ 챔버 표의 빈 문구가 「없음」이 아니라 **이 사유**가 된다
   *
   * ⚠ **왜 있나**(2026-09-21 실측): `GET /samples` 는 **operator/admin 전용**이고 viewer 는
   *   403 `INSUFFICIENT_ROLE` 이다(`app/views/samples.py` `READ_ROLES` · 계약 `listSamples`
   *   `x-required-role` — PII 접촉면). 종전에는 그 403 하나가 **모니터링 화면 전체**를 에러로
   *   덮어서 viewer 가 모듈 카드·그림·비전 탭을 통째로 못 봤다. 모니터링의 주 자료는
   *   `GET /modules` 이고 시료는 **덧붙는 숫자**다 — 부가 자료의 실패가 주 화면을 죽이지 않는다.
   */
  chambersNotice?: string | undefined
  /**
   * 트레이 자리 배치(3×3 + RSLOT).
   *
   * 🔴 **안 넘기는 것이 live 의 정답이다.** 서버에 시료↔칸 대응이 없다(`ChamberRow` 머리말 · BR-31).
   *    안 넘기면 칸 이름만 있는 빈 격자 + **「칸 배정 정보 없음」**이 그려진다.
   * ⚠ **기본값으로 fixture 를 두지 마라.** 종전에 `= DEMO_TRAY_LAYOUT` 이 있었고, live 컨테이너가
   *   이 값을 안 넘기는 바람에 **모듈이 전부 `IDLE` 인 화면의 로터에 `S-1045` 같은 가짜 시료가
   *   돌고 있었다**(2026-09-20 실측). 기본값이 곧 그 사고의 원인이었다.
   */
  tray?: readonly TrayCell[]
  /**
   * 비전 카메라 3대. ⚠ 영상 스트림 경로는 계약에 없다 — 프레임과 메타만 그린다(§10-2).
   * ⚠ **`figureTabs` 배치(장비 관점 · 태블릿 · 패드)의 그림 탭 「비전」만 읽는다.** 콘솔(`sampleTabs`)의 영상은 장비별 보기 상세 카드의
   *   비전 칸(`VisionPane`)이 스트림 설정(`visionStreamFor`)으로 세우고 이 fixture 를 안 읽는다 — live 에 fixture 판정을 그리지 않는다.
   *
   * 🔴 여기도 **기본값을 두지 않는다.** 안 넘기는 것이 live 다(`tray` 와 같은 축).
   * 🔴 **안 넘겨도 「비전」 탭은 선다 — eric 지시 2026-09-21.** 없는 것은 탭이 아니라 **내용**이고,
   *    `FocusFigure` 가 탭 «안»에 「카메라 영상 연결 없음」을 적는다. 종전에 여기서
   *    `vision: visionFeeds !== undefined` 로 탭을 걸렀고 그게 eric 이 지적한 증상이다 —
   *    되살리지 마라.
   */
  visionFeeds?: readonly VisionFeed[]
  access?: ScreenAccess
  /** `/monitoring/:moduleKey` 로 들어왔을 때의 모듈 */
  deviceKey?: MonitoringDeviceKey
  onSelectDevice?: (key: MonitoringDeviceKey) => void
  /**
   * **지금 그 배치를 돌리고 있는 모듈.** 넘어오면 선택이 이 모듈을 따라간다 —
   * eric 지시 2026-09-21 「염색기가 실행 중이면 염색기 탭이 활성화」.
   *
   * 🔴 **화면이 스스로 알아내지 않는다.** 값을 만드는 곳은 `useRunningDeviceKey()` 하나이고
   *    (구독 진행 프레임 — 작업 줄 3행과 **같은 출처**), 여기로 «넘겨받기만» 한다.
   *    그래야 이 화면이 쿼리 제공자 없이도 서고, 시료 관점 · 장비 관점 · 태블릿이 같은 규칙을 쓴다.
   * ⚠ 안 넘기면 따라가기가 **꺼진 것**이다(지금까지의 동작 그대로).
   */
  runningDevice?: MonitoringDeviceKey | undefined
  /**
   * 따라갈 때 부른다. 안 넘기면 `onSelectDevice` 로 떨어진다.
   * 🔴 **`replace` 로 이동해야 한다** — 자동 전환이 뒤로 가기 이력에 쌓이면, 사람이 뒤로 가도
   *    제가 지나온 모듈들을 되짚게 된다. 사람이 «누른» 것만 이력에 남는다.
   */
  onFollowDevice?: (key: MonitoringDeviceKey) => void
  /**
   * 모듈 카드 줄(`.modarea`)에서 **카드 격자 앞**에 끼울 것. 안 넘기면 아무것도 안 그린다.
   *
   * 장비 관점 콘솔이 6축 로봇 레일 카드를 여기 꽂는다 — 그 카드는 **그쪽 앱 안에** 있다.
   * 🔴 서버 목록을 «거르는» 자리가 아니다. `GET /modules` 가 준 4건을 그대로 넘겨주고,
   *    무엇을 그릴지는 받는 쪽이 정한다.
   * ⚠ `ModuleFigureView` 의 `leading`(탭 줄 왼쪽 슬롯)과 **다른 것**이다. 섞지 마라.
   */
  leading?: (modules: readonly ModuleSnapshot[]) => ReactNode
  /**
   * 「전체」 보기에 덧붙일 조각(레일 · 비전 아이콘 · 「작업 후」 트레이).
   * 🔴 **기본은 전부 꺼짐 = 지금 화면 그대로.** 장비 관점 콘솔만 켠다.
   */
  overview?: OverviewParts | undefined
  /** 「3축 로봇」 그림 탭을 붙인다(원심분리기에만 있다). 기본 꺼짐 */
  gantry?: boolean
  /**
   * **시료 흐름 — 시료 관점 콘솔이 켜는 것**(eric 지시 2026-09-21).
   *
   * 넘어오면 화면의 주인공이 장비에서 **시료**로 바뀐다. 두 자리가 달라진다:
   *   ⓐ 모듈 카드 줄 → **단계 줄**(이름이 장비가 아니라 단계 · 아래 줄이 「몇 건이 지났나」)
   *   ⓑ 그림 왼쪽에 **환자별 챔버 카드**가 선다(시료마다 한 장 · 안에 단계 줄)
   *
   * 🔴 **기본은 꺼짐 = 장비 관점 그대로다.** eric 이 못박았다 — 「그림 영역 오른쪽 위 탭의
   *    부품이나 도구들은 그대로 놔둬」. 장비 관점 앱은 이 프롭을 안 넘기므로 **한 픽셀도**
   *    안 바뀐다(`equipmentOverview.test.tsx` 가 그것을 지킨다).
   * ⚠ `docs/concepts.md` §2 의 「기본값은 시료 관점의 지금 동작」과 **방향이 반대인 첫 옵션**이다.
   *   그 규칙의 목적은 「한쪽 때문에 다른 쪽이 흔들리지 않게」인데, 이번에는 바뀌는 쪽이
   *   시료 관점이라 **끄는 쪽이 종전 동작**이 된다. 같은 목적을 지키는 반대 방향이다.
   */
  sampleFlow?: SampleFlowView | undefined
  /**
   * **화면을 무엇으로 가르는가** — eric 지시 2026-09-21.
   *
   * | 값 | 화면 |
   * | --- | --- |
   * | `'figureTabs'`(기본) | 종전 그대로. 그림 탭 줄(전체·트레이·원심분리기·비전)이 오른쪽 위에 서고, 시료 흐름은 그 «왼쪽 열»에 선다 |
   * | `'sampleTabs'` | 그림 탭 줄을 **걷고** 그 자리에 **[시료][장비] 두 탭.** 탭마다 전체 폭이다 |
   *
   * eric 원문: 「오른쪽 위에 전체·트레이·원심분리기·비전 있는 것을 없애고, 그 자리에 왼쪽에
   * 시료, 오른쪽에 장비 이렇게 두 개 탭으로 만들어줘 … 디폴트 탭은 시료 탭이야」
   *
   * 🔴 **기본값이 종전이다.** 장비 관점 콘솔(`apps/console-equipment`)·태블릿·모듈 패드는 이
   *    프롭을 안 넘기므로 **한 픽셀도** 안 바뀐다 — eric: 「장비 console 까지 수정하진 마」.
   *    `equipmentOverview.test.tsx` · `stepActivity.test.tsx` · `monitoring.test.tsx` 가 그것을 잰다.
   * ⚠ `sampleFlow` 와 **다른 축**이다. 저쪽은 「시료 흐름 값이 있느냐」이고 이쪽은 「어떻게
   *   배치하느냐」다. `'sampleTabs'` 인데 `sampleFlow` 가 없으면 시료 탭이 빈다 — 콘솔 라우트는 둘 다 켠다.
   */
  layout?: MonitoringLayout
  /**
   * 🔴 **시료 판을 «볼 수 없는» 사유**(2026-09-26 · viewer). 컨테이너가 `GET /auth/me` 의
   *    `operations` 에 `listSamples` 가 **없다고 확인했을 때만** 넘긴다 — 그때는 요청 자체를 안 쏜다.
   *    넘어오면 ⓐ 첫 보기가 **장비**다(권한 없는 판을 첫 화면으로 열지 않는다)
   *    ⓑ 「시료별 보기」 구획은 **숨기지 않는다** — 눌리고, 판 자리에 이 사유 + 장비로 가는 길이 선다
   * ⚠ `operations` 를 아직 모르면(로그인 직후) 안 넘어온다 — 종전대로 시료가 첫 보기다.
   */
  samplesLocked?: string | undefined
  /**
   * 🔴 **잠긴 시료 판이어도 카드를 세울 수 있다**(eric 2026-09-28 「일단 권한 신경쓰지 말고 보이게 해줘」) — viewer 는
   * `GET /samples` 는 못 받지만 작업의 배정(`sample_assignments`)으로 카드가 선다(`useSampleFlow`). 참이면 잠김 판을
   * 그리지 않고 카드를 그린다. ⚠ 첫 보기(장비)는 그대로다 — 바꾸는 것은 eric 결정이다.
   */
  samplesViaAssignments?: boolean | undefined
  /**
   * **소모품 현황**(eric 2026-09-27 「팁랙의 현재 상황, 전체 개수 중에 몇 개 차 있나 확인하는 대시보드」) — `sampleTabs` 배치의
   * 오른쪽 열 소모품 판(`SupplyPanel`) **한 곳**에 선다. 왼쪽이 시료별 보기든 장비별 보기든 같다(eric 2026-10-02).
   * 🔴 **이 값이 오른쪽 열 자체를 켠다** — 안 넘기면 오른쪽 열이 없다(장비 관점 · 태블릿 · 시험).
   *    ⚠ 오른쪽 열은 탭이 아니다 — 「비전」 탭은 걷혔고 영상은 장비별 보기 상세 카드 안이다(`EquipmentSheet` · eric 2026-10-02).
   * 🔴 **선택기 줄의 요약 카드(`SupplyStrip`)는 걷었다**(eric 2026-10-02 「장비별 보기에서.. 이게 보이는데.. 이거 없애줘」).
   *    시료 서랍이 열려 오른쪽 열이 접힌 동안에는 소모품이 화면에 없다(서랍을 닫으면 돌아온다).
   * 🔴 판 머리를 눌러도 아무것도 안 뜬다(eric 2026-10-02 「안 나오게 해줘」 — 상세 팝오버를 걷었다). 보충만 버튼이다.
   * 🔴 값은 컨테이너가 `supplySummary()` 로 만든다 — 화면은 그리기만 한다. 안 넘기면 판이 없다(장비 관점 · 태블릿).
   * 🔴 시료 판이 잠겨도(viewer) 선다 — 모듈 · 레시피는 viewer 도 본다. 잠긴 것은 시료(PII)뿐이다.
   */
  supplies?: SuppliesView | undefined
  /**
   * **오른쪽 서랍 한 장 — 시료 상세**. 무엇을 꽂을지는 라우트가 정한다(화면은 라우터를 모른다).
   * 🔴 소모품 상세는 없다(eric 2026-10-02 팝오버를 걷었다) — 서랍에도 소모품을 꽂지 마라.
   * 🔴 **왼쪽 「시료 | 장비」 판 안의 오른쪽에 선다**(eric 2026-10-02 「시료 장비 탭 안에서... 오른쪽으로 drawer 나오게 해.
   *    그러니까 소모품 자리는 차지하지 말라고..」). 선택기 줄 · 오른쪽 소모품 열은 서랍이 열려도 그대로다. 「시료」 보기에서만 선다.
   * `sampleTabs` 배치에서만 쓴다. 안 넘기면(`undefined` · `null`) 판이 전체 폭이다.
   */
  drawer?: ReactNode
  /** 모듈 메트릭 아래 또는 통합 콘솔 오른쪽 메트릭 아래에 세우는 이벤트 로그. 컨테이너가 데이터 연결을 맡는다. */
  moduleEvents?: ReactNode
}

/** 소모품 현황 한 벌 — 값 + 항목별 보충(팁 · TC) */
export interface SuppliesView {
  summary: SupplySummary
  /**
   * 항목의 보충 값 — 컨테이너가 `replenishViewOf()` 로 짓는다(권한 · 끊김 · 목 모드 사유 포함). 시약은 `undefined`.
   * ⚠ 2026-10-02 eric 「보충을 빼고..」로 판 머리의 「보충」을 걷었다 — **통합 콘솔 판에는 버튼이 없다**. 염색기·어셈블러 모듈 화면은 상세에서 기존 보충 확인을 연다(2026-10-25).
   *   되살릴 때를 위해 배선은 남겨 둔다(`SupplyPanel` 머리말)
   */
  replenish?: ((key: SupplyKey) => ReplenishView | undefined) | undefined
}

/**
 * 화면 배치. 🔴 기본은 `figureTabs`(종전)다 — 새 값을 기본으로 올리지 마라.
 * - `deviceOnly` — 모듈 앱(원심분리기 · eric 2026-10-16 「모니터링 화면 중에서.. 원심 분리기 탭의 내용만 보여주려고 해」).
 *   통합 콘솔 「장비」 열의 판(`EquipmentSheet`) **하나만** 선다 — 장비 칩 줄 · 시료 · 콘솔 소모품 열 · 보기 줄이 없다. 염색기·어셈블러는 장비 그림에 소모품 상태·상세를 통합한다(2026-10-25).
 *   고른 장비는 `deviceKey` 로 고정이고 따라가기도 하지 않는다(그 앱은 제 장비만 본다).
 */
export type MonitoringLayout = 'figureTabs' | 'sampleTabs' | 'deviceOnly'

/** `deviceOnly` 의 따라가기 대상 — 비어 있어 아무 데도 안 간다(렌더마다 새 배열을 만들지 않게 밖에 둔다) */
const NO_FOLLOW: readonly MonitoringDeviceKey[] = []

/** [시료][장비] 두 탭의 키. 🔴 기본은 **시료**다(eric — 「디폴트 탭은 시료 탭이야」) */

/**
 * 시료 흐름 한 벌 — 컨테이너가 만들어 내려 준다.
 * 🔴 **화면이 계산하지 않는다.** 줄과 셈은 `sampleFlowModel.ts` 가 만들고 여기는 그리기만 한다.
 */
export interface SampleFlowView {
  /**
   * **환자별 챔버 카드 한 벌** — 시료 탭이 그리는 전부다(eric 지시 2026-09-21 오후).
   *
   * 🔴 **종전의 시료 × 단계 격자는 걷었다.** 그것이 말하던 것(시료마다 5단계 중 어디)이
   *    카드 «안»의 단계 줄로 들어왔다 — 같은 시료 ID 와 같은 진행이 두 번 나오던 자리다.
   * 🔴 **모든 시료가 여기 있다.** 레시피 묶음에 못 든 시료도 제 카드로 선다
   *    (`patientChambers()`). 빈 배열이면 화면이 「진행 중인 작업 없음」을 적는다.
   */
  bundles: readonly SampleBundle[]
  /**
   * **배치 단위 단계 5칸** — 🔴 **구역 머리에 한 벌**(eric 2026-09-23 「현재 진행 상태를
   * 한쪽으로 모는 건 어때?」). 종전에는 카드마다 같은 줄이 있었다.
   * 🔴 시료들의 다수결이 아니라 **배치 이벤트**가 출처다 — 구독 `sample_lifecycle` 이
   *    `batch_id` 만 싣고 `sample_id` 를 안 싣는다(실물 18건 전수 2026-09-23).
   */
  batchStages?: readonly FlowCell[] | undefined
  tallies: readonly StageTally[]
  /*
   * ⚠ **염색기 소모품 한 벌(`supplies` — 레시피에서 뽑은 RA1 · RA2 · RC 베이)은 걷었다**
   *   (eric 2026-09-26 「원래 있던 상온 RA1, RA2, RC 이 부분은 빼고」). 오른쪽 판은 이제 장비에
   *   달린 랙만 그려 레시피를 안 읽는다. 되살리지 마라.
   */
  timesNotice?: string | undefined
  /**
   * **도는 작업 레시피의 랩웨어 정의**(`payload.labware[]`) — 소모품 요약(`supplySummary`)의 칸 수 출처이자
   * 소모품 판 랙 그림의 구멍 자리 출처(eric 2026-09-27 「오픈트론의 랩웨어 … 거기서 가지고 와서 그려」).
   * 작업이 없으면 `undefined` 이고 칸 수 · 그림은 번들 사본을 쓴다. 새 요청이 아니다 — 목록이 이미 `payload` 전문을 싣는다.
   */
  deckLabware?: readonly RecipeLabwareDef[] | undefined
  /** 같은 레시피의 `payload.reagents` — 소모품 요약의 «시약을 둔 웰»(`volumeByWell` 의 키) 출처 */
  deckReagents?: Readonly<Record<string, ReagentDef>> | undefined
  /*
   * ⚠ **작업 표기(`batchLabel`)는 걷었다**(eric 2026-09-23 「이 텍스트도 빼줘」) — 레시피
   *   이름은 **상시 작업 줄**이 이미 말한다. 되살리면 같은 값이 한 화면에 두 번 선다.
   */
  /** 시료를 **못 본** 사유(viewer 403 · 조회 중). 있으면 카드 대신 이 문구가 선다 */
  notice?: string | undefined
  /** 구독이 끊겨 「지금」을 모른다 */
  stale?: boolean | undefined
  onOpenSample?: ((sampleId: string) => void) | undefined
  /** 오른쪽 판에 열린 시료(eric 2026-09-27) — 그 카드가 「고른 카드」로 선다 */
  selectedSampleId?: string | undefined
}

const DEVICES: readonly MonitoringDeviceKey[] = ['cent', 'asm', 'stain']

/**
 * 「장비」 탭 칸 안의 장비 칩 — 세 모듈 + **맨 오른쪽 6축 로봇**(eric 2026-10-06 「6축 로봇... 위에다가 넣지 말고. 장비에다가.
 * 6축 로봇을 추가하고.. 6축 로봇 클릭하면 그거만 보이게 해줘.」). 누르면 장비 판이 6축 것만 선다(`EquipmentSheet`).
 * 🔴 **따라가기(`DEVICES`)에는 넣지 않는다** — 이송 구간마다 판이 6축으로 튕기지 않게. 칩으로 고르는 것만 된다.
 */
const EQUIP_CHIPS: readonly MonitoringDeviceKey[] = [...DEVICES, 'robot']

/** 장비 칩의 짧은 이름 키(「장비」 탭 칸이 좁을 때만 보인다) */
const DEVICE_SHORT: Partial<Record<MonitoringDeviceKey, MessageKey>> = {
  cent: 'monitoring.deviceSelect.short.cent',
  asm: 'monitoring.deviceSelect.short.asm',
  stain: 'monitoring.deviceSelect.short.stain',
  robot: 'monitoring.deviceSelect.short.robot',
}

function shortDeviceLabel(key: MonitoringDeviceKey): { short?: string } {
  const message = DEVICE_SHORT[key]
  return message === undefined ? {} : { short: t(message) }
}

export function MonitoringScreen({
  modules = [],
  chambers = [],
  chambersNotice,
  tray,
  visionFeeds,
  access = {},
  deviceKey,
  onSelectDevice,
  runningDevice,
  onFollowDevice,
  leading,
  overview,
  gantry = false,
  sampleFlow,
  layout = 'figureTabs',
  moduleEvents,
  samplesLocked,
  samplesViaAssignments = false,
  supplies,
  drawer,
}: MonitoringScreenProps) {
  const [localDevice, setLocalDevice] = useState<MonitoringDeviceKey>('cent')
  const [component, setComponent] = useState('all')
  /* 모듈 앱(`deviceOnly`) — 이벤트 로그 아래 메트릭 카드 자리. 서기 전에는 `null` 이라 판이 아무 데도 안 그린다 */
  const [metricsHost, setMetricsHost] = useState<HTMLDivElement | null>(null)
  /* 칸 순서와 저장 키는 고정이다. 제목줄에서 접으면 DOM에서 빠지고, 모서리 단추로 복원한다. */
  const samplesCol = useColumnShown('samples')
  const equipmentCol = useColumnShown('equipment')
  const visionCol = useColumnShown('vision')
  const supplyCol = useColumnShown('supply')
  /* 소모품 칸 「좁음」 표지(`data-narrow` · 32rem 이하) — 카드 머리를 두 줄로 나누는 규칙이 읽는다(`monitoring.css` · 컨테이너 쿼리 대신) */
  const supplyColRef = useNarrowFlag<HTMLElement>('data-narrow', 32)
  const sidePanelId = useId()
  const lockedHintId = useId()

  const device = deviceKey ?? localDevice

  const selectDevice = (key: MonitoringDeviceKey) => {
    // 모듈을 바꾸면 사이드 패널이 닫힌다(§14-3)
    setComponent('all')
    setLocalDevice(key)
    onSelectDevice?.(key)
  }

  /*
   * 🔴 **도는 모듈을 따라간다**(eric 지시 2026-09-21). 「전환 사건」에만 반응하므로 사람이
   *    다른 모듈을 눌러 둔 동안에는 가만히 있는다 — 판정은 `useFollowRunningDevice` 에 있다.
   * 🔴 **`DEVICES` 안으로만 간다.** 6축 로봇은 시료 관점에 카드가 없고, 장비 관점에서도
   *    핸드오프는 «지나가는 구간»이라 거기로 튕기면 작업 모듈에서 눈이 떠난다.
   * ⚠ 따라갈 때도 그림 탭은 「전체」로 돌아간다 — 사람이 누른 것과 같은 길(`selectDevice`)이다.
   */
  useFollowRunningDevice({
    running: runningDevice,
    selected: device,
    /* 🔴 6축도 따라간다(eric 2026-10-07 「준비 단계에서.... 6축 로봇쪽 탭으로 포커싱」) — 이송 구간이면 칩 줄 넷째 칸(6축)으로 */
    /* 장비 하나만 보는 배치(모듈 앱)는 따라가지 않는다 — 고를 칸이 없다 */
    selectable: layout === 'deviceOnly' ? NO_FOLLOW : EQUIP_CHIPS,
    onFollow: (key) => {
      setComponent('all')
      setLocalDevice(key)
      ;(onFollowDevice ?? onSelectDevice)?.(key)
    },
  })

  /*
   * 6축 로봇은 부품 탭이 없다 — 팔 하나뿐이라 고를 것이 없다. 탭이 1개 이하면 줄 자체가 사라진다.
   * 🔴 **「비전」 탭은 자료 유무와 무관하게 선다 — eric 지시 2026-09-21.** 여기서 자료로 거르지
   *    마라(`visionFeeds !== undefined` 가 있던 자리다). 비는 것은 탭 «안»이고 그 사유는
   *    `FocusFigure` 가 적는다.
   */
  const tabs = device === 'robot' ? [] : figureTabs(device, { gantry })

  /*
   * ⚠ **시료 탭 오른쪽 데크 판(`DeckSuppliesPanel`)은 걷었다**(eric 2026-09-28 「아까처럼 여기만 남겨두고..
   *   오른쪽은 제거해버려」 — 「여기」 = 선택기 줄의 소모품 요약 칩 · 그 칩 줄도 2026-10-02 에 걷혔다). 되살리지 마라.
   * ⚠ 2026-10-01 회의로 오른쪽에 **소모품 현황 판(`SupplyPanel`)**이 다시 섰다 — 옛 데크 판(`DeckRackFigure` 랙 그림 · 레시피
   *   튜브 랙)이 아니라 머리 글자 + 칸 격자(`SlotGrid`) · 시약 줄 목록(`ReagentList`)을 세운 것이다(아래 `supplyColumn`). 옛 판을 되살리지 마라.
   */

  /*
   * 시료 흐름 한 벌 — **환자별 챔버 카드**(eric 지시 2026-09-21 오후).
   * 🔴 **두 배치가 이것을 나눠 쓴다.** `figureTabs` 에서는 그림 왼쪽 열에, `sampleTabs` 에서는
   *    「시료」 탭의 전체 폭에 그대로 선다 — 안에 든 것은 한 줄도 다르지 않다.
   * ⚠ 감싸는 상자를 두지 않는다 — 구역이 하나라 그림이 곧 그 열이다(종전 `.monitor__flowcol`
   *   은 격자와 그림 둘을 세로로 나누던 자리였고, 격자를 걷으면서 같이 걷었다).
   */
  /* 진행 중인 챔버 아래 「지금 하는 일」 — 그 단계 모듈의 지금 상태(`chamberActivity.ts` · eric 2026-10-08) */
  const chamberActivity = chamberActivityOf(modules, sampleFlow?.stale === true)
  const flowColumn =
    sampleFlow === undefined ? null : (
      <ChamberActivityContext value={chamberActivity}>
        <SampleBundles
          bundles={sampleFlow.bundles}
          canViewPii={canView(access)}
          {...(sampleFlow.batchStages === undefined ? {} : { batchStages: sampleFlow.batchStages })}
          {...(sampleFlow.timesNotice === undefined ? {} : { timesNotice: sampleFlow.timesNotice })}
          {...(sampleFlow.notice === undefined ? {} : { notice: sampleFlow.notice })}
          {...(sampleFlow.stale === undefined ? {} : { stale: sampleFlow.stale })}
          {...(sampleFlow.onOpenSample === undefined
            ? {}
            : { onOpenSample: sampleFlow.onOpenSample })}
          /*
           * 🔴 **상세를 못 여는 사람(viewer)은 카드가 비활성 + 사유다** — 시료 상세(`GET /samples/{id}`)는 `view` 권한이고
           *    viewer 에게 403 이다. 카드는 배정으로 서도(`samplesViaAssignments`) 상세는 못 연다. 숨기지 않는다.
           * ⚠ 권한을 아직 모르면(`permissions` 없음) 막지 않는다 — 열어 보고 서버가 답한다(3값 규율).
           */
          openLocked={
            access.permissions !== undefined && !canView(access)
              ? t('monitoring.samplesLocked')
              : undefined
          }
          {...(sampleFlow.selectedSampleId === undefined
            ? {}
            : { selectedSampleId: sampleFlow.selectedSampleId })}
        />
      </ChamberActivityContext>
    )

  /*
   * 🔴 **오른쪽 열 = 소모품 판 하나**(eric 2026-10-02 「오른쪽에. .비전.. 은 없애..」). 왼쪽이 시료별 보기든 장비별 보기든
   *    **언제나** 선다. 탭이 아니다 — 선택기 줄의 오른쪽 칸은 제목(「소모품」) 한 줄이다(탭 하나짜리 탭 줄을 두지 않는다).
   *    - 소모품 = 소모품 현황 판(`SupplyPanel`) 그대로. 머리 · 칩은 눌러지지 않고 버튼이 없다(2026-10-02 「보충」도 걷었다)
   * ⚠ 같은 날 오전 ~ 오후에는 [소모품][비전] 두 탭이었고 「비전」이 세 모듈 영상 칸을 세로로 세웠다(`VisionColumn`).
   *   eric 「각각의 모듈 영상들... 장비의 상세.. (오른쪽) 내용 위에 넣어줘」로 영상은 장비별 보기 상세 카드의 그 장비 칸
   *   하나가 됐다(`EquipmentSheet` → `VisionPane`). 오른쪽 열에 「비전」 탭 · 영상 칸을 되살리지 마라.
   * ⊕ 2026-10-12 eric 「장비와 소모품 사이에 비전 체크박스랑 텍스트 넣고.. 세 개의 영상을 위에서 아래로 동시에」 — 탭이 아니라 «보기» 체크박스로
   *   켜는 넷째 열(`vision`)로 돌아왔다. 처음은 꺼져 있다(`monitoringColumns.ts` `OFF_BY_DEFAULT`). 장비 상세 카드의 그 장비 칸은 그대로다.
   * 🔴 **시료를 못 봐도 열은 선다** — 소모품은 모듈 · 레시피(viewer 200)다.
   * 🔴 소모품을 안 넘기면(장비 관점 · 태블릿 · 시험) 열이 없다 — 이 열을 켜는 것은 통합 콘솔 라우트(`supplies`) 하나다.
   * 🔴 **시료 서랍이 열려도 열은 그대로다**(eric 2026-10-02 「소모품 자리는 차지하지 말라고..」). 서랍은 왼쪽 「시료 | 장비」
   *   판 «안»의 오른쪽에 선다(아래 `drawerShown`). ⚠ 2026-09-28 ~ 10-02 에는 서랍이 열리면 이 열을 접고 그 자리에 서랍을 세웠다 —
   *   되살리지 마라.
   */
  /* 서랍이 꽂혔는가 — 꽂힌 서랍이 있을 때만(시험이 고른 키만 넘기면 서랍은 안 선다) */
  const drawerPane = layout === 'sampleTabs' && drawer != null
  /*
   * 🔴 **서랍은 「시료」 보기 판 안에서만 선다.** 「장비」 보기로 바꾸면 그 판에는 제 상세 카드(`EquipmentSheet` 오른쪽)가 있어
   *    서랍이 그 위를 덮는다 — 그동안은 그리지 않는다. 주소(`/sample/:id`)는 그대로라 「시료」로 돌아오면 같은 서랍이 다시 선다.
   *    닫는 것은 사람의 × 뿐이다(라우트 머리말 — 자동 전환이 읽던 상세를 닫지 않는다).
   */
  const drawerShown = drawerPane && samplesCol.shown
  const sideColumn = layout === 'sampleTabs' && supplies !== undefined
  const supplyShown = sideColumn && supplyCol.shown
  /* 비전 열도 소모품처럼 통합 콘솔 라우트(`supplies` 를 넘기는 자리)에서만 — 장비 관점 앱 · 태블릿은 그대로다 */
  const visionShown = sideColumn && visionCol.shown
  const shownCount = [samplesCol.shown, equipmentCol.shown, visionShown, supplyShown].filter(
    Boolean,
  ).length

  if (layout === 'deviceOnly') {
    /*
     * 🔴 **판은 통합 콘솔 「장비」 열과 같은 컴포넌트다**(`EquipmentSheet`) — 모듈 앱용으로 다시 그리지 않는다.
     *    그림 · 메트릭 · 깜빡임 · 영상 칸이 콘솔과 늘 같다.
     */
    /*
     * 🔴 **왼쪽에 그 모듈의 비전 영상 한 칸**(eric 2026-10-27 「좌측으로.. 그림은 오른쪽으로」) —
     *    콘솔 비전 열(`monvision`)과 같은 카드 · 같은 영상 칸(`VisionPane`)이고 장비가 하나라 칸도 하나다. 영상 경로가 없거나 끊기면
     *    칸 안에 사유가 선다(칸은 언제나 선다 · CLAUDE.md 「비전 탭은 항상 있다」). 카메라가 없는 6축은 칸이 없다.
     */
    const visionDevice = isVisionDevice(device) ? device : undefined
    return (
      <div
        className={cx(
          'monitor monitor--device',
          visionDevice !== undefined && 'monitor--devvision',
        )}
      >
        <h2 className="a-sr">{monitoringDeviceName(device)}</h2>
        {visionDevice === undefined ? null : (
          <div
            className={cx(
              'moncol__body monvision monvision--one',
              moduleEvents != null && 'monvision--events',
            )}
          >
            {/* 모듈 앱: 비전 → 3축 로봇 → 장비 카드 → 이벤트 로그(eric 2026-10-27). 콘솔의 배치는 그대로다. */}
            <VisionPane bare device={visionDevice} fullscreen />
            <div className="monvision__metrics" ref={setMetricsHost} />
            {moduleEvents}
          </div>
        )}
        <div className="moncol__body">
          <EquipmentSheet
            access={access}
            chambers={samplesAt(chambers, findModule(modules, device))}
            chambersNotice={chambersNotice}
            deviceKey={device}
            modules={modules}
            stale={sampleFlow?.stale === true}
            tabs={tabs}
            tray={tray}
            {...(visionDevice === undefined ? {} : { metricsTarget: metricsHost })}
            moduleSupplies={device === 'stain' || device === 'asm' ? supplies : undefined}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cx('monitor', layout === 'sampleTabs' && 'monitor--cols')}>
      {/*
        와이어프레임 모니터링에는 제목 줄(.ahead)이 없다 — 유일하게 없는 화면이다.
        그래도 화면에는 이름이 있어야 해서 시각적으로만 숨긴다(스크린리더는 읽는다).
      */}
      <h2 className="a-sr">{t('screen.monitoring.title')}</h2>

      {/*
        🔴 **이 화면은 6축 로봇 자리를 «스스로» 그리지 않는다**(시료 관점 기본값).
        ⚠ **서버 목록을 «거른» 것이 아니다.** `GET /modules` 는 6축(`collaborative_6axis`)을
          계속 4건 중 하나로 주고 우리도 그대로 받는다 — **여기서 그리지 않을 뿐**이다.
          시스템 › 모듈 표에는 그 행이 그대로 있다.
        장비 관점 콘솔은 `leading` 으로 그 자리를 꽂는다.
        ⚠ **예외 — 통합 콘솔 배치(`sampleTabs`)의 「장비별 보기」는 장비 칩 줄에 「6축 로봇」 칸이 있다**(eric 2026-10-06
          「6축 로봇... 위에다가 넣지 말고. 장비에다가.」) — 고르면 판이 6축 장비 카드(팔 그림)와 6축 상세만 선다.
          카드 줄 배치(장비 관점 · 태블릿 · 패드)는 위 규칙 그대로다.
        ⚠ 3축 로봇(gantry)과 다른 장비다. 그쪽은 그림 탭·레일 쪽 이야기다.
      */}
      {layout === 'sampleTabs' ? null : (
        <div className="modarea">
          {leading?.(modules)}
          <div className="modgrid">
            {DEVICES.map((key, index) => {
              const snapshot = findModule(modules, key)
              /*
               * 🔴 **시료 관점이면 카드가 「단계」다**(eric 지시 2026-09-21). 같은 자리·같은 동작
               *    (누르면 그 장비의 그림으로 간다 · 도는 곳을 따라간다)이고 **읽히는 말만** 바뀐다.
               *    장비 관점은 `sampleFlow` 를 안 넘기므로 `ModuleCard` 그대로다.
               */
              if (sampleFlow !== undefined) {
                const tally = sampleFlow.tallies.find((item) => item.device === key)
                return (
                  <StageCard
                    deviceKey={key}
                    index={index}
                    key={key}
                    onSelect={() => selectDevice(key)}
                    selected={device === key}
                    snapshot={snapshot}
                    {...(tally === undefined ? {} : { tally })}
                  />
                )
              }
              return (
                <ModuleCard
                  key={key}
                  deviceKey={key}
                  onSelect={() => selectDevice(key)}
                  /* 🔴 못 본 것(`chambersNotice`)은 **수를 만들지 않는다** — 0 건과 다른 사실이다 */
                  sampleCount={
                    chambersNotice === undefined ? samplesAt(chambers, snapshot).length : undefined
                  }
                  selected={device === key}
                  snapshot={snapshot}
                />
              )
            })}
          </div>
        </div>
      )}

      {/*
        🔴 **그림 자체는 어느 배치에서도 안 바뀐다.** 갈리는 것은 «어떻게 놓느냐»뿐이고,
           그림은 두 길 모두 `FocusFigure` 한 파일에서 나온다 — 두 벌로 만들지 마라.
        🔴 패널 본체는 패드와 **같은 컴포넌트**다. 여기서 화면을 다시 조립하지 마라.
        🔴 **고른 모듈의 시료만 내려간다**(`samplesAt`). 칸은 몰라도 «어느 장비에 있나»는
           서버가 준다(`Sample.current_module`) — 그 축으로 좁히는 것이 live 가 할 수 있는
           가장 좁은 진실이다. 전체 목록을 내리면 염색기 패널에 원심 시료가 섞인다.
      */}
      {layout === 'sampleTabs' ? (
        <div className="moncols">
          <CollapsedColumn column="samples" col={samplesCol} label={t('tab.monitoring.samples')} />
          <CollapsedColumn
            column="equipment"
            col={equipmentCol}
            label={t('monitoring.deviceSelect.equipment')}
          />
          {sideColumn ? (
            <CollapsedColumn column="supply" col={supplyCol} label={t('monitoring.side.title')} />
          ) : null}
          {sideColumn ? (
            <CollapsedColumn column="vision" col={visionCol} label={t('figTab.vision')} />
          ) : null}
          {shownCount === 0 ? <ScreenState text={t('monitoring.view.none')} /> : null}
          {samplesCol.shown ? (
            <MonColumn
              column="samples"
              onCollapse={() => samplesCol.setShown(false)}
              title={t('tab.monitoring.samples')}
              {...(samplesLocked === undefined
                ? {}
                : { hint: samplesLocked, hintId: lockedHintId })}
            >
              {samplesLocked !== undefined && !samplesViaAssignments ? (
                /* 🔴 막다른 판을 만들지 않는다 — 사유 한 줄. 장비 · 소모품 열은 옆에 그대로 선다(PII 가 아니다) */
                <ScreenState text={samplesLocked} />
              ) : (
                <div className={cx('moncol__body', drawerShown && 'moncol__body--drawer')}>
                  {flowColumn}
                  {/*
                      🔴 **서랍은 시료 열 «안»의 오른쪽 몫**(eric 2026-10-02) — 판과 같은 격자 칸에 겹쳐 서고 판은 서랍 몫만큼 비켜 선다
                         (`monitoring.css` 의 `.moncol__body--drawer`). 장비 · 소모품 열은 자리 · 폭 · 내용이 그대로다.
                    */}
                  {drawerShown ? drawer : null}
                </div>
              )}
            </MonColumn>
          ) : null}
          {equipmentCol.shown ? (
            <MonColumn
              column="equipment"
              onCollapse={() => equipmentCol.setShown(false)}
              head={
                /*
                 * 장비 칩 — 제목 「장비」 옆 한 줄(`devsel--sub`). 🔴 제목 단추와 형제다(단추 안에 tablist 를 넣지 않는다).
                 * 접힌 띠에서는 칩이 안 선다 — 고른 장비는 상태(`device`)로 남아 펼치면 그대로다.
                 */
                <ViewSegments
                  className="devsel devsel--sub devsel__equip"
                  label={t('monitoring.deviceSelect.modules')}
                  onSelect={(key) => selectDevice(key)}
                  options={EQUIP_CHIPS.map((key) => ({
                    key,
                    /*
                     * 장비 이름 꼴(염색«기») — 단계 이름(염색)이 아니다. eric 2026-09-22 「염색 말고 염색기」.
                     * 🔴 6축 칩은 「운송 로봇」(eric 2026-10-13 「탭 부분 말이야」 → 판 안 전부) — 판 제목 · 단계 띠와 같은 한 곳(`monitoringDeviceName`)
                     */
                    label: monitoringDeviceName(key),
                    /* 칸이 좁을 때만 보이는 짧은 글자 — 접근 이름 · `title` 은 긴 이름 그대로 */
                    ...shortDeviceLabel(key),
                    className: 'devsel__seg',
                    /*
                     * 🔴 지금 그 작업을 돌리는 장비 — 탭 오른쪽 위 초록 점(eric 2026-10-15 「현재 진행 중인 모듈에.. 초록 점을 해당 탭 오른쪽 위에」).
                     *    근거는 따라가기와 같은 한 값(`runningDevice` · 서버 진행 프레임 · 이송이면 운송 로봇). 모듈 상태 어휘로 짐작하지 않는다
                     *    (종전 `state === 'RUNNING'` 은 그런 상태 값이 없어 한 번도 안 켜졌다)
                     */
                    running: key === runningDevice,
                  }))}
                  selected={device}
                />
              }
              title={t('monitoring.deviceSelect.equipment')}
            >
              {/*
               * 🔴 영상은 이 판 «왼쪽» 상세 카드 맨 위 — 고른 장비 칸 하나(`EquipmentSheet` · eric 2026-10-02 · 좌우는 2026-10-06)
               * 🔴 **6축 로봇은 장비 칩 줄의 넷째 칸이다**(eric 2026-10-06) — 고르면 판이 6축 것만 선다(왼쪽 6축 상세 · 오른쪽 팔 그림).
               * 🔴 따라가기는 칩 줄 전부(`EQUIP_CHIPS` · 셋 + 6축)다(eric 2026-10-07 「준비 단계에서.... 6축 로봇쪽 탭으로 포커싱 가능해?」) —
               *   이송 구간(이 배치의 `robot` 자원 점유 · `useRunningDeviceKey`)이면 판이 6축으로 넘어가고, 다음 장비가 돌면 그 장비로 간다.
               */}
              <div className="moncol__body">
                <EquipmentSheet
                  access={access}
                  chambers={samplesAt(chambers, findModule(modules, device))}
                  chambersNotice={chambersNotice}
                  deviceKey={device}
                  modules={modules}
                  stale={sampleFlow?.stale === true}
                  tabs={tabs}
                  tray={tray}
                  events={moduleEvents}
                  stepNoteInHeader
                />
              </div>
            </MonColumn>
          ) : null}
          {supplyShown && supplies !== undefined ? (
            <MonColumn
              onCollapse={() => supplyCol.setShown(false)}
              sectionRef={supplyColRef}
              column="supply"
              title={t('monitoring.side.title')}
            >
              <div className="moncol__body monside" id={sidePanelId}>
                <SupplyPanel replenish={supplies.replenish} summary={supplies.summary} />
              </div>
            </MonColumn>
          ) : null}
          {visionShown ? (
            /*
             * 비전 열 — 원심분리기 · 어셈블러 · 염색기 영상 셋을 위에서 아래로(eric 2026-10-12 「세 개의 영상을 위에서 아래로 동시에」).
             * 칸마다 장비 상세 카드와 같은 영상 칸(`VisionPane`)이다 — 스트림 설정이 없으면 사유 한 줄, 판정은 그리지 않는다
             */
            <MonColumn
              onCollapse={() => visionCol.setShown(false)}
              column="vision"
              title={t('figTab.vision')}
            >
              <div className="moncol__body monvision">
                {VISION_DEVICES.map((key) => (
                  <VisionPane device={key} fullscreen key={key} />
                ))}
              </div>
            </MonColumn>
          ) : null}
        </div>
      ) : (
        /*
         * 종전 배치 — 그림 탭 줄이 오른쪽 위에 서고, 시료 흐름은 그 «왼쪽 열»에 선다.
         * 🔴 **장비 관점 콘솔·태블릿·모듈 패드가 이 길로 온다.** 한 픽셀도 바꾸지 마라.
         */
        <div className={cx('monitor__body', sampleFlow !== undefined && 'monitor__body--flow')}>
          {flowColumn}
          <ModuleFigureView
            access={access}
            chambers={samplesAt(chambers, findModule(modules, device))}
            chambersNotice={chambersNotice}
            component={component}
            deviceKey={device}
            modules={modules}
            onChangeComponent={setComponent}
            tabs={tabs}
            tray={tray}
            visionFeeds={visionFeeds}
            {...(overview === undefined ? {} : { overview })}
          />
        </div>
      )}
    </div>
  )
}

/** 고정 순서의 칸 — 제목과 접기 단추. 끈 칸은 같은 저장 상태로 복원한다. */
function MonColumn({
  onCollapse,
  column,
  title,
  head,
  hint,
  hintId,
  sectionRef,
  children,
}: {
  onCollapse: () => void
  /** 칸 상자를 재는 쪽의 끈(소모품 칸 「좁음」 표지) */
  sectionRef?: Ref<HTMLElement> | undefined
  column: MonitorColumn
  /** 화면 제목과 접근 이름 */
  title: string
  /** 제목 옆 장비 칩 */
  head?: ReactNode
  /** 열에 붙는 «설명»(viewer 의 시료 사유) — `aria-describedby` 로만 */
  hint?: string | undefined
  hintId?: string | undefined
  children: ReactNode
}) {
  return (
    <section
      aria-label={title}
      className={`moncol moncol--${column}`}
      data-column={column}
      ref={sectionRef}
      {...(hint === undefined || hintId === undefined ? {} : { 'aria-describedby': hintId })}
    >
      <div className="moncol__head">
        <div className="moncol__bar">
          <h3 className="moncol__title">{title}</h3>
          {head}
          <button
            type="button"
            className="moncol__collapse"
            aria-label={`${title} ${t('strip.eventsCollapse')}`}
            aria-expanded={true}
            onClick={onCollapse}
          >
            {/*
             * 닫기 X — 모달 닫기(`DialogSurface` `.a-modal__close`)와 같은 선 아이콘(eric 2026-10-29 「- 대시를 X 로」 · 10-30 「X 는 X 로」 —
             * 상자 없이 X 만 · `monitoring.css` `.moncol__collapse`). 동작은 접기 그대로 · 접근 이름은 단추의 「… 접기」
             */}
            <Icon name="close" />
          </button>
        </div>
      </div>
      {hint === undefined || hintId === undefined ? null : (
        <span className="a-sr" id={hintId}>
          {hint}
        </span>
      )}
      {children}
    </section>
  )
}

/**
 * **구획 선택기 — 시안 `.seg`(보기 방식) · `.seg--sub`(장비 셋)**(디자인팀 2026-10-01). 한 컴포넌트가 두 줄을 그린다 —
 * 겉모습은 클래스(`devsel` · `devsel--sub`)가 가른다.
 *
 * 🔴 `role="tablist"` + `role="tab"` + `aria-selected` — 고른 것을 색이 아니라 상태가 말한다. 흑백 스킨은 테 · 굵기로 선다.
 * 🔴 `atoms/Segmented` 를 안 쓴 까닭: 그쪽은 알약(`radiogroup` · 강조 채움 · 999px)이고, 시안은 8px 채움 상자 안의
 *    흰 구획(그림자)이다. 겉모습 · 역할이 둘 다 달라 원자를 덮지 않고 이 화면의 것으로 둔다.
 * 키보드: ← → 가 구획을 옮기고(포커스도 같이) Home · End 가 양 끝이다.
 */
function ViewSegments<K extends string>({
  className,
  label,
  options,
  selected,
  onSelect,
}: {
  className: string
  /** 묶음의 접근 이름(`aria-label`) */
  label: string
  options: readonly {
    key: K
    label: string
    className: string
    /** 구획에 붙는 «설명»(사유) — 이름을 흐리지 않게 `aria-describedby` 로만 */
    describedBy?: string | undefined
    title?: string | undefined
    /** 지금 작업을 돌리는 장비 — 오른쪽 위 초록 점 + 접근 이름 끝말 「실행 중」 */
    running?: boolean | undefined
    /**
     * 칸이 좁을 때 대신 보이는 짧은 글자(장비 칩 — `monitoring.css` 의 컨테이너 질의가 `data-short` 를 그린다). 주면 접근 이름
     * (`aria-label`) · `title` 이 긴 이름(`label`)으로 고정된다 — 어느 글자가 보이든 스크린리더는 긴 이름을 읽는다.
     * DOM 글자(`textContent`)는 언제나 긴 이름이다 — 짧은 글자는 CSS 가 그리는 그림이다.
     */
    short?: string | undefined
  }[]
  selected: K
  onSelect: (key: K) => void
}) {
  const focusKey = options.some((option) => option.key === selected) ? undefined : options[0]?.key
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = options.findIndex((option) => option.key === selected)
    const step =
      event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowLeft'
          ? -1
          : event.key === 'Home'
            ? -options.length
            : event.key === 'End'
              ? options.length
              : 0
    if (step === 0 || at < 0) return
    event.preventDefault()
    const next = Math.max(0, Math.min(options.length - 1, at + step))
    const target = options[next]
    if (target === undefined || target.key === selected) return
    onSelect(target.key)
    event.currentTarget
      .querySelector<HTMLElement>(`[data-seg="${target.key}"]`)
      ?.focus({ preventScroll: true })
  }
  return (
    <div aria-label={label} className={className} onKeyDown={onKeyDown} role="tablist">
      {options.map((option) => (
        <button
          aria-selected={option.key === selected}
          className={option.className}
          data-seg={option.key}
          key={option.key}
          onClick={() => onSelect(option.key)}
          role="tab"
          /*
           * 고른 구획만 탭 차례에 든다(tablist 관례) — 나머지는 화살표로 간다.
           * 🔴 고른 것이 이 묶음 밖이면(장비 판이 6축 상세 — 칩 셋에 6축이 없다) 첫 구획이 탭 차례를 받는다. 안 그러면 묶음이 키보드로 안 닿는다
           */
          tabIndex={option.key === (focusKey ?? selected) ? 0 : -1}
          type="button"
          {...(option.describedBy === undefined ? {} : { 'aria-describedby': option.describedBy })}
          {...(option.short === undefined
            ? option.title === undefined
              ? {}
              : { title: option.title }
            : {
                'aria-label': option.running
                  ? `${option.label} — ${t('monitoring.deviceSelect.running')}`
                  : option.label,
                title: option.title ?? option.label,
              })}
        >
          <span
            className="devsel__name"
            {...(option.short === undefined ? {} : { 'data-short': option.short })}
          >
            {option.label}
          </span>
          {option.running ? (
            <span className="devsel__run" data-running="">
              <span className="a-sr">{t('monitoring.deviceSelect.running')}</span>
            </span>
          ) : null}
        </button>
      ))}
    </div>
  )
}

/**
 * 단계 줄 카드 — **시료 관점 전용.** 모듈 카드와 같은 자리·같은 동작이고 «읽히는 말»이 다르다.
 *
 * **공정 칸 3개가 같은 템플릿이다**(디자인팀 시안 2026-10-01 · `docs/design/DESIGN-CHANGES.md` §2 · 「개발 규칙」).
 *
 * ```
 * ┌──────────────────────────────────┐
 * │ ① 원심분리기            [분주 중] │  ← 번호 표 · 장비 이름 · 상태 배지(서버 값 — 진행 중이면 현재 동작)
 * │ ████████████░░░░░░░░░░░░░░░░░░░░ │  ← 12px 막대(3px 모서리) — 두 수의 비. 전체를 모르면 빗금
 * │ 3 / 5 시료 지남              60% │  ← 셈 줄. 🔴 단위는 종전 그대로 «시료» 다(스텝이 아니다)
 * │ 실패 1건 · 응답 없음              │  ← 덧붙는 사실들. 🔴 서로 «자리를 다투지 않는다»
 * └──────────────────────────────────┘
 * ```
 *
 * 🔴 **상태는 클래스 하나(`stage--done|run|wait|pause|stop`)로만 갈린다.** 칸의 구성은 어느 상태에서도 같고
 *    바뀌는 것은 색 · 글자 굵기뿐이다(판정은 `monitoringModel.ts` 의 `stageStateOf` — 있는 값 둘로만). 강조(옅은 강조 면 + 안쪽 테)는 **진행 중인 칸 하나**에만 선다.
 *    완료된 칸도 셈과 100% 를 **그대로** 둔다 — 단계가 넘어가도 정보가 사라지지 않는다(시안 §2 「완료된 공정」).
 * 🔴 **색만으로 말하지 않는다.** 상태 배지의 글자(「대기」 · 「분주 중」 · 「일시정지」 · 「오류」)와 셈(「5 / 5 · 100%」)이
 *    같은 사실을 말하고, 흑백 앱에서는 막대 채움 · 테 · 글자 굵기만으로 선다.
 * 🔴 **한 칸에 여러 사실을 다투게 두지 않는다.** `ModuleCard` 의 `.mcard__note` 는 3항 분기라
 *    「응답 없음」이 뜨면 시료 수가 **사라졌다**. 시료가 주인공인 화면에서 그건 못 쓴다 —
 *    여기서는 셈이 제 줄을 갖고, 덧붙는 사실은 아래 줄에 **나란히** 선다.
 * 🔴 **장비 상태 배지는 그대로 둔다.** 「이 단계를 하는 장비가 지금 어떤가」는 여전히 사실이고
 *    서버가 준 값이다(`ModuleCard` 와 같은 규율 — 스냅샷이 없으면 지어내지 않고 `—` 다).
 *    시안의 「현재 동작 배지(예: 이동 중)」가 곧 이 배지다 — 진행 중이면 서버 상태(분주 중 · 이동 중 …)가 그 글자다.
 */
/** 단계 카드 첫 줄 — 장비 이름 꼴. 6축(`robot`)은 시료 관점에 카드가 없다 */
function stageCardName(device: MonitoringDeviceKey): string {
  if (device === 'cent') return t('stageCard.cent')
  if (device === 'asm') return t('stageCard.asm')
  if (device === 'stain') return t('stageCard.stain')
  return enumLabel('enum.sampleStage', stageOfDevice(device))
}

function StageCard({
  deviceKey,
  index,
  snapshot,
  selected,
  onSelect,
  tally,
}: {
  deviceKey: MonitoringDeviceKey
  /** 칸의 차례(0부터) — 번호 표 「1 · 2 · 3」 */
  index: number
  snapshot: ModuleSnapshot | undefined
  selected: boolean
  onSelect: () => void
  tally?: StageTally | undefined
}) {
  const stale = snapshot?.stale === true
  const state = snapshot?.state
  const hold = holdKindOf(snapshot)
  const alarms = activeAlarmCount(snapshot)
  const stageState = stageStateOf(snapshot, tally)
  /*
   * 🔴 **셀 것이 없으면 세지 않는다.** 도는 작업이 없을 때 「0/0 지남」이라고 적으면
   *    「시료가 0건인 작업이 돌고 있다」로 읽힌다 — 실물에서 그렇게 나와서 걷었다
   *    (2026-09-21 · 배치가 `handoff_unconfirmed` 로 중단된 직후 화면).
   * 🔴 **못 봤다(`tally` 없음)와 0 건은 다른 사실이다** — 못 봤으면 빗금 막대 + 「전체 수량 집계 중」(시안 §2),
   *    0 건이면 빈 막대 + `—` 다.
   */
  const known = tally !== undefined && tally.total > 0
  const pending = tally === undefined

  return (
    <button
      aria-pressed={selected}
      className={cx(
        'mcard',
        'stage',
        `stage--${stageState}`,
        snapshot?.state === 'RUNNING' && 'mcard--run',
        stale && 'mcard--stale',
      )}
      onClick={onSelect}
      type="button"
    >
      <span className="mcard__head stage__head">
        {/* 번호 표 — 차례는 카드 줄의 DOM 차례와 같다(원심 → 어셈블 → 염색). 이름이 곧 뜻이라 보조기술에는 숨긴다 */}
        <span aria-hidden="true" className="stage__mark">
          {String(index + 1)}
        </span>
        {/* 카드 첫 줄은 장비 이름 꼴(원심분리기·어셈블러·염색) — 단계 줄의 짧은 이름과 다르다(ko.ts 주석) */}
        <span className="mcard__name stage__name">{stageCardName(deviceKey)}</span>
        {/* 🔴 낡았으면 배지가 «마지막 값»임을 글자로 말한다(정본 07 2026-09-25 · 마지막 값 병기) */}
        <StatusBadge
          label={staleValue(enumLabel('enum.moduleState', state), stale)}
          status={state ?? ''}
        />
        <HoldBadge kind={hold} />
      </span>
      {/*
       * 진행 막대 — 🔴 **응답에 있는 두 수의 «비»다.** 서버가 안 주는 진행률을 만들어 그리는 것이 아니다
       *    (`marked` · `total` 둘 다 셈한 값). 바로 아래 줄이 같은 값을 숫자로 적고 있고, 막대는 그 수를 한눈에
       *    보이게 거들 뿐이다 — `role="img"` 로 이름을 붙여 스크린리더에도 같은 말이 간다.
       */}
      <StageTrack
        label={
          known
            ? t('monitoring.stage.marked', {
                marked: String(tally.marked),
                total: String(tally.total),
              })
            : pending
              ? t('monitoring.stage.totalPending')
              : t('mask.none')
        }
        pending={pending}
        {...(known ? { ratio: tally.marked / tally.total } : {})}
      />
      <span className="scard__lines">
        <span className="stage__meta">
          {known ? (
            <>
              <span className="scard__tally">
                <b className="scard__count">{String(tally.marked)}</b>
                <span className="scard__of">/ {String(tally.total)}</span>
                <span className="scard__unit">{t('monitoring.stage.markedUnit')}</span>
              </span>
              <span className="stage__pct num">
                {`${String(Math.round((tally.marked / tally.total) * 100))}%`}
              </span>
            </>
          ) : pending ? (
            <span className="scard__tally scard__tally--pending">
              {t('monitoring.stage.totalPending')}
            </span>
          ) : (
            <>
              <span className="scard__tally scard__tally--none">{t('mask.none')}</span>
              <span className="stage__pct num">{t('mask.none')}</span>
            </>
          )}
        </span>
        <span className="scard__notes">
          {stale ? (
            <StaleMark detail={staleDetail(snapshot?.last_seen)} label={t('safety.moduleStale')} />
          ) : null}
          {snapshot?.attribution === 'inconclusive' ? (
            <Badge variant="inconclusive">{t('monitoring.attributionInconclusive')}</Badge>
          ) : null}
          {/*
           * 🔴 **활성 알람은 «있는 자리»를 잇는 것이다**(2026-09-22). 서버가 모듈마다
           *    `active_alarm_count` 를 주는데(사이드 패널 「정보」가 이미 그 칸을 쓴다) 카드
           *    줄에는 한 자도 없었다 — 염색기가 ERROR 알람과 함께 섰는데 모니터링 어디에도
           *    자국이 없었다. **새 영역을 만들지 않고** 이 덧붙는 줄에 얹는다.
           * 🔴 `0`·`null` 이면 아예 안 그린다(`activeAlarmCount`).
           */}
          {alarms === undefined ? null : (
            <b className="scard__note scard__note--fail">
              {t('monitoring.activeAlarms', { count: String(alarms) })}
            </b>
          )}
          {tally !== undefined && tally.failures > 0 ? (
            <b className="scard__note scard__note--fail">
              {t('monitoring.stage.failures', { count: String(tally.failures) })}
            </b>
          ) : null}
        </span>
      </span>
    </button>
  )
}

/**
 * 모듈 카드 — 이름 + 상태 배지 + 시료 수 + (stale 이면) 응답 없음 (1611행).
 *
 * 🔴 **시료 수는 서버가 준 값으로만 센다**(`Sample.current_module`). 0 건이면 **적지 않는다** —
 *    「0」과 「모른다」를 가르기 위해서다. 목록에 모듈 축이 없으면(demo) `samplesAt` 이 전건을
 *    주므로 세 카드에 같은 수가 붙는데, 그건 **그 목록이 모듈을 구분하지 않는다는 사실 그대로**다.
 * 🔴 **`undefined` 는 「못 봤다」다**(2026-09-21). `GET /samples` 가 403 이거나 아직 안 왔을 때
 *    호출부가 이것을 넘긴다 — 0 을 넘기면 **「이 장비에 시료가 없다」는 거짓**이 된다.
 *    둘 다 화면에는 안 적히지만, **수를 만들어 내지 않는 것**이 여기서 지키는 규율이다.
 *
 * 🔴 **스냅샷이 없으면 상태를 «만들지 않는다» — 배지가 `—` 다**(2026-09-21). 종전에는
 *    `?? 'IDLE'` 이라 `GET /modules` 가 그 모듈을 아예 안 준 자리에 화면이 「대기」라고 단언했다.
 *    「대기」는 서버가 `IDLE` 이라고 말했을 때만 뜬다.
 * ⚠ 장비 관점의 6축 레일 카드(`RobotRail.tsx` — 2026-10-06 이 폴더로 올렸다)도 **같이** 고쳤다 —
 *   한쪽만 되돌리면 같은 줄에 선 카드 4장 중 하나만 다른 말을 한다.
 */
function ModuleCard({
  deviceKey,
  snapshot,
  selected,
  onSelect,
  sampleCount,
}: {
  deviceKey: MonitoringDeviceKey
  snapshot: ModuleSnapshot | undefined
  selected: boolean
  onSelect: () => void
  sampleCount: number | undefined
}) {
  const stale = snapshot?.stale === true
  // 🔴 없는 상태를 지어내지 않는다 — 판정(「없으면 `—` · 모르는 값이면 원값」)은 `enumLabel()` 이 한다
  const state = snapshot?.state
  const hold = holdKindOf(snapshot)
  const alarms = activeAlarmCount(snapshot)

  return (
    <button
      aria-pressed={selected}
      className={cx(
        'mcard',
        snapshot?.state === 'RUNNING' && 'mcard--run',
        stale && 'mcard--stale',
      )}
      onClick={onSelect}
      type="button"
    >
      <span className="mcard__head">
        <span className="mcard__name">
          {moduleLabel(deviceKey)}
          <StatusBadge
            label={staleValue(enumLabel('enum.moduleState', state), stale)}
            status={state ?? ''}
          />
          <HoldBadge kind={hold} />
        </span>
        {/*
         * ⚠ 이 자리는 **한 칸을 여럿이 다툰다**(위 `StageCard` 머리말). 차례를 정한 축은
         *   「지금 사람이 알아야 하는 것」이고, **활성 알람이 시료 수보다 앞**이다 —
         *   모듈이 알람과 함께 섰는데 카드가 시료 수만 적고 있으면 안 된다.
         */}
        {stale ? (
          <StaleMark detail={staleDetail(snapshot?.last_seen)} label={t('safety.moduleStale')} />
        ) : snapshot?.attribution === 'inconclusive' ? (
          <Badge variant="inconclusive">{t('monitoring.attributionInconclusive')}</Badge>
        ) : alarms !== undefined ? (
          <b className="mcard__note">{t('monitoring.activeAlarms', { count: String(alarms) })}</b>
        ) : sampleCount !== undefined && sampleCount > 0 ? (
          <span className="mcard__note">
            {t('monitoring.sampleCount', { count: String(sampleCount) })}
          </span>
        ) : null}
      </span>
    </button>
  )
}

/**
 * **모듈이 스스로 섰다는 배지** — `ModuleSnapshot.hold.kind`(계약 선언 · 실물 실측 2026-09-22).
 *
 * 🔴 **상태 배지를 «덮지» 않고 옆에 선다.** 염색기가 `state: DISPENSING` 인 채로
 *    `hold.kind: reagent_exhausted` 로 섰던 회차에 화면은 「분주 중」이라고만 말했다 —
 *    2분 동안 0 스텝인데 멈췄다는 자국이 한 자도 없었다. 둘 다 서버가 준 사실이라 둘 다 적는다.
 * 🔴 **색을 늘리지 않는다**(방안 A 는 강조 1 + 위험 1 뿐이다). 이것은 «주의»지 «실패»가
 *    아니므로 위험색을 쓰지 않고 **일시정지 얼굴**(`❙❙` 글리프 + 그 테)을 빌려 쓴다 —
 *    `statusKind` 가 `PAUSED` 를 그 용도의 화면 별칭으로 이미 두고 있다. 알람이 같이 있으면
 *    위험색은 그쪽(「활성 알람 N건」)이 쓴다.
 * ⚠ **모르는 `kind` 는 원값 그대로** 나간다(`enumLabel`). 지어내지 않는다.
 */
function HoldBadge({ kind }: { kind: string | undefined }) {
  if (kind === undefined) return null
  return (
    <StatusBadge
      label={t('monitoring.hold', { kind: enumLabel('enum.holdKind', kind) })}
      originalValue={kind}
      status="PAUSED"
    />
  )
}

/** 접힌 띠 이름 넷(시료 · 장비 · 소모품 · 비전) — 아래 `CollapsedColumn` 부르는 자리와 같은 사전 키다 */
const RAIL_LABEL_KEYS = [
  'tab.monitoring.samples',
  'monitoring.deviceSelect.equipment',
  'monitoring.side.title',
  'figTab.vision',
] as const

/** 가장 긴 띠 이름 — 모든 띠 이름 칸이 이 키를 차지한다(eric 2026-10-29 「글자 세 개에 맞게 … 띄워줘」) */
function longestRailLabel(): string {
  return RAIL_LABEL_KEYS.map((key) => t(key)).reduce((a, b) =>
    [...b].length > [...a].length ? b : a,
  )
}

/** 2026-10-28 eric 결정: 접힌 칸은 왼쪽에 고유 순서대로 모이는 세로 띠다. */
function CollapsedColumn({
  column,
  col,
  label,
}: {
  column: MonitorColumn
  col: { shown: boolean; setShown: (next: boolean) => void }
  label: string
}) {
  if (col.shown) return null
  return (
    <button
      aria-expanded={false}
      aria-label={`${label} ${t('strip.eventsExpand')}`}
      className="moncollapsed"
      data-column={column}
      onClick={() => col.setShown(true)}
      type="button"
    >
      <span aria-hidden="true">›</span>
      <span className="moncollapsed__name">
        <span className="moncollapsed__text">{label}</span>
        {/* 가장 긴 띠 이름을 보이지 않게 겹쳐 이름 칸 키를 맞춘다(`monitoring.css` `.moncollapsed__name`) */}
        <span aria-hidden="true" className="moncollapsed__sizer">
          {longestRailLabel()}
        </span>
      </span>
    </button>
  )
}
